import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Avatar, IconButton, Tooltip, TruncateText } from '../ui';
import { HeartIcon } from '../ui/icons';
import AvatarPadrao from '../../images/svgs/avatar-padrao.svg';
import { getSmallestImage } from '../../shared/util/imageUtils';
import {
  formatDataToProductCourse,
  getDescription
} from '../../shared/util/cursosOnlineUtils';
import RatingRow from './components/RatingRow/RatingRow';
import ProductTag from './components/ProductTag/ProductTag';
import PriceFooter from './components/PriceFooter/PriceFooter';
import styles from './ProductCard.module.css';

export default function ProductCard({
  product,
  isFavorito,
  onToggleFavorite,
  linkSeller = true
}) {
  const { t } = useTranslation('home');
  const [liked, setLiked] = useState(!!isFavorito);

  useEffect(() => {
    setLiked(!!isFavorito);
  }, [isFavorito]);

  const ft = product.sebraetecFichaTecnica;
  const curso = product.curso;
  const saas = product.saasProduto;

  let link,
    image,
    tagKey,
    tagLabel,
    title,
    category,
    rating,
    showHeart,
    showAvatar,
    avatarSrc,
    sellerName,
    sellerId;

  switch (product.tipo) {
    case 'SEBRAETEC_FICHA_TECNICA': {
      link = `/sebraetec/${(ft && (ft.slug || ft.id)) || product.id}`;
      image = ft && getSmallestImage(ft.imgUrl, ft.imgUrl && ft.imgUrl.url);
      tagKey = 'sebraetec';
      tagLabel = t('TAG_SEBRAETEC');
      title = ft && ft.nome;
      category =
        ft && ft.tema && ft.subtema
          ? `${ft.tema.nome} - ${ft.subtema.nome}`
          : (ft && ft.tema && ft.tema.nome) || '';
      rating = ft && ft.mediaAvaliacao;
      showHeart = true;
      showAvatar = false;
      break;
    }
    case 'CONSULTORIA': {
      link = `/consultoria/${product.id}`;
      image = getSmallestImage(product.fotoUrlResponsive, product.fotoUrl);
      tagKey = 'consultoria';
      tagLabel = t('TAG_CONSULTORIA');
      title = product.nome;
      const consultoriaTema = product.consultoria && product.consultoria.tema;
      const consultoriaSubtema =
        product.consultoria && product.consultoria.subtema;
      category = consultoriaTema
        ? consultoriaSubtema
          ? `${consultoriaTema.nome} - ${consultoriaSubtema.nome}`
          : consultoriaTema.nome
        : product.categoria;
      rating = product.mediaAvaliacao;
      showHeart = true;
      showAvatar = true;
      avatarSrc = product.pessoa && product.pessoa.fotoUrl;
      sellerName = product.pessoa && product.pessoa.nome;
      sellerId = product.pessoa && product.pessoa.id;
      break;
    }
    case 'SAAS_PRODUTO': {
      link = `/sd/${(saas && saas.slug) || product.id}`;
      image = getSmallestImage(
        product.imagem && product.imagem.imagemResponsive,
        product.imagem && product.imagem.imagemOriginal
      );
      tagKey = 'solucao';
      tagLabel = t('TAG_SOLUCAO');
      title = product.nome;
      category =
        saas &&
        saas.landingPage &&
        saas.landingPage.secaoCabecalho &&
        saas.landingPage.secaoCabecalho.descricao;
      rating = product.mediaAvaliacao;
      showHeart = false;
      showAvatar = true;
      avatarSrc = product.pessoa && product.pessoa.fotoUrl;
      sellerName = product.pessoa && product.pessoa.nome;
      sellerId = product.pessoa && product.pessoa.id;
      break;
    }
    case 'CURSO': {
      link = `/cursos-online/${(curso && (curso.slug || curso.id)) ||
        product.id}`;
      const courseData = curso ? formatDataToProductCourse(curso) : {};
      image = getSmallestImage(courseData.imagemResponsive, courseData.cover);
      tagKey = 'curso';
      tagLabel = t('TAG_CURSO');
      title = curso && curso.nome;
      category = curso ? getDescription(curso) : '';
      rating = curso && curso.mediaAvaliacao;
      showHeart = true;
      showAvatar = true;
      avatarSrc = curso && curso.pessoa && curso.pessoa.fotoUrl;
      sellerName = curso && curso.pessoa && curso.pessoa.nome;
      sellerId = curso && curso.pessoa && curso.pessoa.id;
      break;
    }
    default:
      return null;
  }

  const sellerTo = linkSeller && sellerId ? `/perfil/${sellerId}` : undefined;

  return (
    <Card variant='product' interactive className={styles.root}>
      <Link to={link} className={styles.cardLink} aria-label={title}>
        <div className={styles.media}>
          {image && <img className={styles.image} src={image} alt='' />}
          <div className={styles.gradient} />
          <ProductTag tagKey={tagKey}>{tagLabel}</ProductTag>
        </div>
        <div className={styles.body}>
          <TruncateText lines={2} className={styles.title}>
            {title}
          </TruncateText>
          <TruncateText lines={2} className={styles.category}>
            {category}
          </TruncateText>
          <RatingRow value={rating} />
        </div>
        <PriceFooter product={product} />
      </Link>
      {showHeart && (
        <IconButton
          variant='floatHeart'
          className={styles.heart}
          aria-label={t('FAVORITE_ARIA')}
          onClick={() => {
            setLiked(!liked);
            if (onToggleFavorite) onToggleFavorite(product);
          }}>
          <HeartIcon
            width={30}
            height={19}
            filled={liked}
            className={liked ? styles.heartIconLiked : styles.heartIcon}
          />
        </IconButton>
      )}
      {showAvatar && (
        <Tooltip title={sellerName} className={styles.avatar}>
          <Avatar
            size='sm'
            src={avatarSrc || AvatarPadrao}
            alt={sellerName || ''}
            to={sellerTo}
            aria-label={sellerTo ? sellerName || undefined : undefined}
            className={styles.avatarImg}
          />
        </Tooltip>
      )}
    </Card>
  );
}

ProductCard.propTypes = {
  product: PropTypes.shape({
    id: PropTypes.string,
    tipo: PropTypes.string.isRequired,
    nome: PropTypes.string,
    sebraetecFichaTecnica: PropTypes.object,
    curso: PropTypes.object,
    saasProduto: PropTypes.object,
    consultoria: PropTypes.object,
    pessoa: PropTypes.object
  }).isRequired,
  isFavorito: PropTypes.bool,
  onToggleFavorite: PropTypes.func,
  linkSeller: PropTypes.bool
};
